'use client';

import { css, cx } from '@onbeam/business-styled-system/css';
import { TargetsSectionProps } from '../TargetsSection/TargetsSection.types';
import { text, vstack } from '@onbeam/business-styled-system/patterns';
import { TimelineDot } from './TimelineDot';
import { timelineItems } from './TimelineSection.data';
import { TimelineItem } from './TimelineItem';
import { Fragment, useEffect, useRef, useState } from 'react';
import { useBreakpoint } from '@onbeam/utils';
import { token } from '@onbeam/business-styled-system/tokens';

export const TimelineSection = ({
  className,
  ...props
}: TargetsSectionProps) => {
  const barRef = useRef<HTMLDivElement>(null);
  const isMobile = useBreakpoint({ max: token('breakpoints.md') });
  const [progress, setProgress] = useState(0);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Reset on breakpoint
  useEffect(() => {
    const handleScroll = () => {
      if (!barRef.current) return;
      const { top, height } = barRef.current.getBoundingClientRect();
      const yMargin = 0.3;
      const vh = window.innerHeight;
      const travel = Math.max(height - vh * (1 - yMargin - yMargin), 1);

      const offsetTop = top - vh * yMargin;
      const sectionProgress = Math.min(Math.max(-offsetTop / travel, 0), 1);

      const yStart = 0;
      const yMiddle = 0.5;
      const yEnd = 1;
      let y: number;
      if (sectionProgress <= yMiddle) {
        y = 0 + sectionProgress * 2 * (yMiddle - yStart);
      } else {
        y = yMiddle + (sectionProgress - yMiddle) * 2 * (yEnd - yMiddle);
      }

      setProgress(y);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isMobile]);

  const height = timelineItems.length * (isMobile ? 285 : 245);

  return (
    <section
      className={cx(
        vstack({
          gap: '10',
          textAlign: 'center',
          align: 'center',
        }),
        className,
      )}
      {...props}
    >
      <h2
        className={text({
          style: 'subtitle-lg',
          maxW: '[43.125rem]',
          md: { textStyle: 'subtitle-xl' },
        })}
      >
        <span>How it works.</span>{' '}
        <span className={text({ color: 'mono.tertiary' })}>
          Your journey from idea to launch on Beam.
        </span>
      </h2>
      <div
        ref={barRef}
        className={css({
          rounded: 'full',
          w: '[0.25rem]',
          bg: 'mono.quinary',
          position: 'relative',
          mr: '[15rem]',

          md: {
            mr: '0',
          },
          _before: {
            content: '""',
            position: 'absolute',
            top: '0',
            insetX: '0',
            h: '[calc(100% * var(--progress))]',
            bg: 'mono.primaryBg',
            rounded: 'full',
          },
          _after: {
            content: '""',
            position: 'absolute',
            bottom: '0',
            insetX: '0',
            h: '[14%]',
            background:
              '[linear-gradient(180deg, transparent -56.06%, token(colors.mono.primaryFg) 100%)]',
          },
        })}
        style={{
          height: `${height / 16}rem`,
          '--progress': progress,
        }}
        aria-hidden
      >
        {timelineItems.map((item, index) => {
          const itemPosition = (index + 1) / (timelineItems.length + 1);
          const position = index % 2 === 1 ? 'right' : 'left';
          return (
            <Fragment key={index}>
              <TimelineItem
                {...item}
                position={position}
                style={{
                  top: `calc(${itemPosition * 100}% - 3.125rem)`,
                }}
              />
              <TimelineDot
                isActive={itemPosition <= progress}
                style={{
                  top: `${itemPosition * 100}%`,
                }}
              />
            </Fragment>
          );
        })}
      </div>
    </section>
  );
};
